import type { NotificationItem } from "../api/notifications";

export type NotificationPreferences = {
    pushNotifications: boolean;
    emailNotifications: boolean;
    soundEffects: boolean;
    notifyMessages: boolean;
    notifySpaces: boolean;
    notifyVault: boolean;
    notifyMentions: boolean;
    notifyReactions: boolean;
    notifySecurity: boolean;
};

const STORAGE_KEY = "blackspace.settings.notifications";

const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
    pushNotifications: true,
    emailNotifications: false,
    soundEffects: true,
    notifyMessages: true,
    notifySpaces: true,
    notifyVault: true,
    notifyMentions: true,
    notifyReactions: true,
    notifySecurity: true,
};

let audioContext: AudioContext | null = null;
let audioPrimed = false;

function bool(value: unknown, fallback: boolean) {
    return typeof value === "boolean" ? value : fallback;
}

export function readNotificationPreferences(): NotificationPreferences {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return {
            pushNotifications: bool(parsed.pushNotifications, DEFAULT_NOTIFICATION_PREFERENCES.pushNotifications),
            emailNotifications: bool(parsed.emailNotifications, DEFAULT_NOTIFICATION_PREFERENCES.emailNotifications),
            soundEffects: bool(parsed.soundEffects, DEFAULT_NOTIFICATION_PREFERENCES.soundEffects),
            notifyMessages: bool(parsed.notifyMessages, DEFAULT_NOTIFICATION_PREFERENCES.notifyMessages),
            notifySpaces: bool(parsed.notifySpaces, DEFAULT_NOTIFICATION_PREFERENCES.notifySpaces),
            notifyVault: bool(parsed.notifyVault, DEFAULT_NOTIFICATION_PREFERENCES.notifyVault),
            notifyMentions: bool(parsed.notifyMentions, DEFAULT_NOTIFICATION_PREFERENCES.notifyMentions),
            notifyReactions: bool(parsed.notifyReactions, DEFAULT_NOTIFICATION_PREFERENCES.notifyReactions),
            notifySecurity: bool(parsed.notifySecurity, DEFAULT_NOTIFICATION_PREFERENCES.notifySecurity),
        };
    } catch {
        return { ...DEFAULT_NOTIFICATION_PREFERENCES };
    }
}

export function writeNotificationPreferences(input: Partial<NotificationPreferences>) {
    const cleanInput = Object.fromEntries(
        Object.entries(input).filter(([, value]) => value !== undefined)
    ) as Partial<NotificationPreferences>;
    const next = { ...readNotificationPreferences(), ...cleanInput };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
}

export function notificationPreferenceAllows(item: NotificationItem, preferences = readNotificationPreferences()) {
    const type = String(item.type || "system");
    const text = `${item.title || ""} ${item.body || ""}`.toLowerCase();
    const metadata = item.metadata || {};

    if (type === "message" || type === "message_request" || type === "friend_request") {
        return preferences.notifyMessages;
    }
    if (type === "note_shared") {
        return preferences.notifyVault;
    }
    if (type === "space_mention") {
        return preferences.notifyMentions;
    }
    if (type.startsWith("space_") || type.startsWith("event_")) {
        return preferences.notifySpaces;
    }
    if (type === "comment" || type === "reply") {
        const mentionLike = Boolean(metadata.mention || metadata.mentionedUserId || metadata.mentionedUsername)
            || text.includes("mentioned")
            || text.includes("mention");
        return mentionLike ? preferences.notifyMentions : preferences.notifyReactions;
    }
    if (type === "authority" || type === "system") {
        return preferences.notifySecurity;
    }

    return true;
}

function getAudioContext() {
    if (audioContext) return audioContext;
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return null;
    audioContext = new AudioContextCtor();
    return audioContext;
}

export function primeNotificationSound() {
    if (audioPrimed) return;

    const prime = async () => {
        const ctx = getAudioContext();
        if (!ctx) return;
        try {
            if (ctx.state === "suspended") await ctx.resume();
            audioPrimed = true;
        } catch {
            // Browsers are allowed to block audio until a later user gesture.
        }
    };

    const once = () => {
        void prime();
        window.removeEventListener("pointerdown", once);
        window.removeEventListener("keydown", once);
        window.removeEventListener("touchstart", once);
    };

    window.addEventListener("pointerdown", once, { passive: true });
    window.addEventListener("keydown", once);
    window.addEventListener("touchstart", once, { passive: true });
}

export async function playNotificationSound() {
    const ctx = getAudioContext();
    if (!ctx) return;

    try {
        if (ctx.state === "suspended") await ctx.resume();

        const now = ctx.currentTime;
        const gain = ctx.createGain();
        const first = ctx.createOscillator();
        const second = ctx.createOscillator();

        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.06, now + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);

        first.type = "sine";
        first.frequency.setValueAtTime(660, now);
        first.frequency.exponentialRampToValueAtTime(880, now + 0.12);

        second.type = "triangle";
        second.frequency.setValueAtTime(1320, now + 0.08);
        second.frequency.exponentialRampToValueAtTime(990, now + 0.28);

        first.connect(gain);
        second.connect(gain);
        gain.connect(ctx.destination);

        first.start(now);
        second.start(now + 0.08);
        first.stop(now + 0.28);
        second.stop(now + 0.34);
    } catch {
        // Notification sound is nice-to-have; never interrupt the app for it.
    }
}

export async function requestBrowserNotificationPermission() {
    if (!("Notification" in window)) return "unsupported";
    if (Notification.permission !== "default") return Notification.permission;
    try {
        return await Notification.requestPermission();
    } catch {
        return Notification.permission;
    }
}

export function showActiveBrowserNotification(item: NotificationItem, onClick?: () => void) {
    if (!("Notification" in window) || Notification.permission !== "granted") return;

    try {
        const notification = new Notification(item.title || "Blackspace", {
            body: item.body || "New activity in Blackspace.",
            icon: "/logo192.png",
            badge: "/favicon-32x32.png",
            tag: item.id || undefined,
            renotify: false,
            silent: true,
        });
        notification.onclick = () => {
            window.focus();
            onClick?.();
            notification.close();
        };
    } catch {
        // Some browsers restrict Notification construction in active tabs.
    }
}
